import React, { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';

import type { ILead } from 'src/services/api';
import { useLeadsPageContext } from 'src/pages/landlord/leads/provider';
import TabsBar from 'src/components/tabs-bar';
import TableHeader, { TableHeaderCell } from 'src/components/table/TableHeader';
import LeadsTablePagination from 'src/pages/landlord/leads/components/table/LeadsTablePagination';
import DesktopLeadsTableRow from 'src/pages/landlord/leads/components/table/DesktopLeadsTableRow';

const LEADS_ROWS_PER_PAGE = 25;

interface Props {
    title?: string,
    leads?: ILead[],
}

export default function DesktopLeadsTable({
    title,
    leads,
}: Props) {
    const { selectedLeadUuids, setSelectedLeadUuids } = useLeadsPageContext();

    const [page, setPage] = useState(1);

    const numberOfLeads = leads?.length || 0;

    const startIndex = (page - 1) * LEADS_ROWS_PER_PAGE;

    let endIndex = page * LEADS_ROWS_PER_PAGE;
    if (endIndex > numberOfLeads) {
        endIndex = numberOfLeads;
    }

    const currentPageLeads = useMemo(() => {
        if (!leads) {
            return [];
        }

        return leads.slice(startIndex, endIndex);
    }, [leads, page, LEADS_ROWS_PER_PAGE, startIndex, endIndex]);

    // Reset page when leads change
    useEffect(() => {
        setPage(1);
    }, [leads?.map(lead => lead.uuid).join(',')]);

    const allSelected = currentPageLeads.every((lead) => {
        return selectedLeadUuids.includes(lead.uuid);
    });

    const selectAll = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (allSelected) {
            setSelectedLeadUuids([]);
        } else {
            setSelectedLeadUuids(currentPageLeads.map((lead) => lead.uuid));
        }
    };

    // Reset selected leads when page changes
    useEffect(() => {
        setSelectedLeadUuids([]);
    }, [page]);

    return (
        <>
            <TabsBar
                trailing={
                    <LeadsTablePagination
                        leads={leads}
                        page={page}
                        setPage={setPage}
                        rowsPerPage={LEADS_ROWS_PER_PAGE}
                    />
                }
            >
                <Typography
                    variant="h3"
                    sx={{
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        margin: 0,
                    }}
                >{title}</Typography>
            </TabsBar>

            <TableContainer component={Paper} elevation={0} square>
                <Table sx={{ tableLayout: 'fixed' }}>
                    <TableHeader>
                        <TableHeaderCell width={40}>
                            <Checkbox
                                checked={allSelected}
                                onClick={selectAll}
                                sx={{
                                    padding: 0,
                                    width: 20,
                                    height: 20,
                                    '& svg': { fontSize: 20, fill: 'white' },
                                }}
                            />
                        </TableHeaderCell>
                        <TableHeaderCell width="25%">Name</TableHeaderCell>
                        <TableHeaderCell width="20%">Stage</TableHeaderCell>
                        <TableHeaderCell width="20%">Source</TableHeaderCell>
                        <TableHeaderCell width="15%">Labels</TableHeaderCell>
                        <TableHeaderCell width="20%">Property</TableHeaderCell>
                        <TableHeaderCell width="20%">Contact</TableHeaderCell>
                        <TableHeaderCell width={40} padding="none" />
                    </TableHeader>
                    <TableBody>
                        {currentPageLeads.map((lead) => (
                            <DesktopLeadsTableRow key={lead.uuid} lead={lead} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
